import { useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Stack,
  Checkbox,
  Typography,
  Box,
  Skeleton,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material";
// components
import {
  CompanyAvatar,
} from '../../sections/company';
import {
  CrewAvatar,
} from '../../sections/profile';
import {
  TodoEditDialog,
  TodoDeleteDialog,
} from './';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
// utils
import axios from '../../utils/axios';
import { mutate } from "swr";
import { fKrDateAbsoulte } from '../../utils/formatTime';


const Line = styled('div')({
  '@keyframes stretch': {
    to: { width: '100%' }
  },
  position: 'relative',
  top: '13px',
  left: 0,
  width: 0,
  height: '2px',
  background: '#000',
  borderRadius: '10px',
  animation: 'stretch 1s ease 1',
  animationFillMode: 'forwards',
})

export default function TodoList({companyId, todos, isActive, isValidating}) {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const smUp = useResponsive('up', 'sm');
  const mdUp = useResponsive('up', 'md');

  const [isOpenEditTodo, setIsOpenEditTodo] = useState(false);
  const [isOpenDeleteTodo, setIsOpenDeleteTodo] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [animateIndex, setAnimateIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openEditDeleteTodo = async (todo, index) => {
    if(!isActive) return ;
    if(isSubmitting) return ;
    if(todo) setSelectedTodo(todo);
    if(index) setSelectedIndex(index);
    closeDeleteTodo();
    setIsOpenEditTodo(true);
  }

  const closeEditTodo = async () => {
    setIsOpenEditTodo(false);
  }

  const openDeleteTodo = async () => {
    if(isSubmitting) return ;
    setIsOpenEditTodo(false);
    setIsOpenDeleteTodo(true);
  }

  const clearSelection = async () => {
    setSelectedTodo({});
    setSelectedIndex(null);
    setAnimateIndex(null);
  }

  const closeDeleteTodo = async () => {
    setIsOpenDeleteTodo(false);
  }

  const editTodo = async (content, targetDate) => {
    try {
      setIsSubmitting(true);
      await axios.post(
        `/api/works/todo/crew/company/${companyId}/${selectedTodo.id}/edit/`,
        { content, target_date: targetDate }
      );
      enqueueSnackbar('투두를 수정하였습니다.');
      await mutate(`/api/works/todo/crew/company/${companyId}/`);
      mutate(`/api/works/todo/crew/company/${companyId}/history/`);
      closeEditTodo();
      clearSelection();
      setIsSubmitting(false);
    } catch (err) {
      console.error(err);
      enqueueSnackbar('처리중 에러가 발생하였습니다. 관리자에 문의해 주세요.', { variant: 'error' });
      setIsSubmitting(false);
    }
  }

  const doneTodo = async (e, todo, passedIndex) => {
    try {
      e.stopPropagation();
      setIsSubmitting(true);
      setAnimateIndex(passedIndex);

      await axios.post(
        `/api/works/todo/crew/company/${companyId}/${todo.id}/done/`
      );
      enqueueSnackbar('투두를 완료 처리하였습니다.');
      await mutate(`/api/works/todo/crew/company/${companyId}/`);
      mutate(`/api/works/todo/crew/company/${companyId}/history/`);
      clearSelection();
      setIsSubmitting(false);
    } catch (err) {
      console.error(err);
      enqueueSnackbar('처리중 에러가 발생하였습니다. 관리자에 문의해 주세요.', { variant: 'error' });
      setIsSubmitting(false);
    }
  }

  const deleteTodo = async () => {
    try {
      setIsSubmitting(true);
      await axios.post(
        `/api/works/todo/crew/company/${companyId}/${selectedTodo.id}/delete/`,
      );
      enqueueSnackbar('투두를 삭제하였습니다.');
      await mutate(`/api/works/todo/crew/company/${companyId}/`);
      mutate(`/api/works/todo/crew/company/${companyId}/history/`);
      closeDeleteTodo();
      clearSelection();
      setIsSubmitting(false);
    } catch (err) {
      console.error(err);
      enqueueSnackbar('처리중 에러가 발생하였습니다. 관리자에 문의해 주세요.', { variant: 'error' });
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Typography variant="h6" sx={{ px: 3, pt: 2.5 }}>
        미완료 데일리 투두
      </Typography>
      {isValidating && todos?.length === 0 && (
        <Stack sx={{ px : 3, my: 2, py: 1 }} spacing={3}>
          <Skeleton height={56} />
          <Skeleton height={56} />
          <Skeleton height={56} />
        </Stack>
      )}
      {!isValidating && todos?.length === 0 && (
        <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
          투두를 추가해주세요.
        </Typography>
      )}

      {mdUp && todos?.length > 0 && (
        <Stack sx={{ mt: 3, mx: 3 }} spacing={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={5}>
            <Typography variant="subtitle2" sx={{ textAlign: "center", maxWidth: '40px', minWidth: '40px', color: "text.disabled" }}>
              완료
            </Typography>
            <Typography variant="subtitle2" sx={{ textAlign: "center", width: 1, color: "text.disabled" }}>
              투두 업무
            </Typography>
            <Typography variant="subtitle2" sx={{ textAlign: 'center', maxWidth: '180px', minWidth: '180px', color: "text.disabled" }}>
              완료 목표일
            </Typography>
            <Typography variant="subtitle2" sx={{ textAlign: "center", maxWidth: '200px', minWidth: '200px', color: "text.disabled" }}>
              투두 작성자
            </Typography>
          </Stack>
          <Divider />
        </Stack>
      )}

      {/* pc */}
      {mdUp && todos.map((todo, index) => (
        <Stack
          key={index} direction="row" alignItems="center" justifyContent="space-between" spacing={5}
          sx={{
            my: 2, py: 1, px: 3,
            ':hover': {
              backgroundColor: 'background.neutral',
              cursor: 'pointer',
            },
            ':active': {
              backgroundColor: 'grey.300',
            },
          }}
          onClick={() => openEditDeleteTodo(todo, index)}
        >
          <Stack direction="row" alignItems="center" sx={{ width: 1 }}>
            <Checkbox
              sx={{ mr: {xs: 2.5, sm: 3} }}
              checked={(index === animateIndex)}
              onClick={(e) => doneTodo(e, todo, index)}
              disabled={!isActive}
            />
            <Box sx={{ p: 0, m: 0 }}>
              <Stack display="inline-block">
                {(index === animateIndex) && (<Line />)}
                <Typography variant="subtitle1" sx={{ wordBreak: 'break-all' }}>
                  {todo.content}
                </Typography>
              </Stack>
            </Box>
          </Stack>
          <Typography sx={{ color: "text.secondary", textAlign: 'center', maxWidth: '180px', minWidth: '180px' }}>
            {fKrDateAbsoulte(todo.target_date)}{todo.target_date ? '까지' : ''}
          </Typography>
          {todo.created_crew && (
            <CrewAvatar hashId={user?.crew?.randomhash} nickname={user?.crew?.nickname} sx={{ maxWidth: '200px', minWidth: '200px' }} />
          )}
          {todo.created_company && (
            <CompanyAvatar companyId={companyId} sx={{ maxWidth: '200px', minWidth: '200px' }} />
          )}
        </Stack>
      ))}

      {/* mo */}
      {!mdUp && todos.map((todo, index) => (
        <Stack
          key={index} direction="row" alignItems="center" spacing={1}
          sx={{
            px: 2, py: 2,
            ':active': {
              backgroundColor: 'background.neutral',
            },
          }}
          onClick={() => openEditDeleteTodo(todo, index)}
        >
          <Checkbox
            checked={(index === animateIndex)}
            onClick={(e) => doneTodo(e, todo, index)}
            disabled={!isActive}
          />
          <Stack spacing={.5}>
            <Box sx={{ p: 0, m: 0 }}>
              <Stack display="inline-block">
                {(index === animateIndex) && (<Line />)}
                <Typography variant="subtitle2" sx={{ wordBreak: 'break-all' }}>
                  {todo.content}
                </Typography>
              </Stack>
            </Box>
            {todo.created_crew && (
              <CrewAvatar hashId={user?.crew?.randomhash} width={20} height={20} nameVariant="caption" nickname={user?.crew?.nickname} sx={{ maxWidth: '70vw' }} />
            )}
            {todo.created_company && (
              <CompanyAvatar companyId={companyId} width={20} height={20} nameVariant="caption" sx={{ maxWidth: '70vw' }} />
            )}
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {fKrDateAbsoulte(todo.target_date)}{todo.target_date ? '까지' : ''}
            </Typography>
          </Stack>
        </Stack>
      ))}

      <TodoEditDialog isOpen={isOpenEditTodo} closeDialog={closeEditTodo} selectedIndex={selectedIndex} todo={selectedTodo}  edit={editTodo} openDelete={openDeleteTodo} isSubmitting={isSubmitting} />
      <TodoDeleteDialog isOpen={isOpenDeleteTodo} closeDialog={() => openEditDeleteTodo()} deleteTodo={deleteTodo} isSubmitting={isSubmitting} />
    </>
  )
}
